// src/layouts/limiteGastos/index.js
import React, { useState, useEffect, useMemo } from "react";
import { firestore } from "firebaseConfig";
import { collection, doc, updateDoc, getDocs, writeBatch, getDoc, deleteField } from "firebase/firestore";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import Slider from "@mui/material/Slider";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { getAuth } from "firebase/auth";
import { useMaterialUIController } from "context";
import { formatCurrency } from "examples/FormatCurrency";

/* ===== Helpers ===== */
const parseCurrencyValue = (valueString) => {
  if (!valueString) return 0;
  if (typeof valueString === "number") return valueString;
  let cleanedValue = valueString.toString().replace(/[^0-9,.]/g, "");
  if (cleanedValue.includes(",") && cleanedValue.includes(".")) {
    cleanedValue = cleanedValue.replace(/\./g, "");
    cleanedValue = cleanedValue.replace(",", ".");
  } else if (cleanedValue.includes(",") && !cleanedValue.includes(".")) {
    cleanedValue = cleanedValue.replace(",", ".");
  } else if (cleanedValue.includes(",")) {
    const parts = cleanedValue.split(",");
    const lastPart = parts.pop();
    const integerPart = parts.join("");
    cleanedValue = `${integerPart}.${lastPart}`;
  }
  return parseFloat(cleanedValue) || 0;
};
const formatNumberToCurrency = (value) =>
  Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const unmaskCurrency = (s) => Number(String(s).replace(/[^\d]/g, "")) / 100 || 0;
const formatCurrencyBR = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const monthKeyNow = () => new Date().toISOString().slice(0, 7);

/* ===== Component ===== */
function LimiteGastos() {
  const [tabIndex, setTabIndex] = useState(0);
  const [categoriasDespesasFixas, setCategoriasDespesasFixas] = useState([]);
  const [categoriasDespesasVariaveis, setCategoriasDespesasVariaveis] = useState([]);

  // dialogs padrão
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(null);
  const [valorLimite, setValorLimite] = useState("");

  // resumo/topo
  const [receitaMensal, setReceitaMensal] = useState("");
  const [controller] = useMaterialUIController();
  const { darkMode, currency } = controller;

  // WIZARD minimalista (passos: 0=Receita, 1=Poupança, 2=Categorias, 3=Revisão)
  const [openWizard, setOpenWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [savingsPct, setSavingsPct] = useState(0.2);
  const [limites, setLimites] = useState({}); // {catId: number}
  const [planningExists, setPlanningExists] = useState(false);

  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  /* ===== Carregar dados do localStorage (resumo) ===== */
  useEffect(() => {
    const savedData = localStorage.getItem("budgetSummary");
    if (savedData) {
      const parsed = JSON.parse(savedData);
      setReceitaMensal(parsed.receitaMensal || "");
    }
  }, []);

  /* ===== Carregar categorias / reset de consumo mensal ===== */
  const loadCategorias = async () => {
    if (!userId) return;

    const now = new Date();
    const mesAtual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    const categoriasRef = collection(firestore, "usuarios", userId, "categorias");
    const data = await getDocs(categoriasRef);
    let categorias = data.docs.map((docu) => ({ ...docu.data(), id: docu.id }));

    for (let categoria of categorias) {
      const ultimoReinicio = categoria.ultimoReinicioConsumo || "2000-01";
      if (ultimoReinicio !== mesAtual) {
        const docRef = doc(firestore, "usuarios", userId, "categorias", categoria.id);
        await updateDoc(docRef, { consumo: 0, ultimoReinicioConsumo: mesAtual });
        categoria.consumo = 0;
        categoria.ultimoReinicioConsumo = mesAtual;
      }
    }

    setCategoriasDespesasFixas(categorias.filter((cat) => cat.tipo === "despesaFixa"));
    setCategoriasDespesasVariaveis(categorias.filter((cat) => cat.tipo === "despesaVariavel"));

    // iniciar limites do wizard com valores atuais
    const base = {};
    categorias.forEach((c) => (base[c.id] = Number(c.limite || 0)));
    setLimites(base);
  };

  const loadReceitaMensal = async () => {
    if (!userId) return;
    const userRef = doc(firestore, "usuarios", userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const savedReceita = userDoc.data().receitaMensal || "";
      setReceitaMensal(savedReceita);
      localStorage.setItem("budgetSummary", JSON.stringify({ receitaMensal: savedReceita }));
    }

    // existe planejamento salvo p/ o mês?
    const budgetRef = doc(firestore, "budgets", userId, "months", monthKeyNow());
    const bSnap = await getDoc(budgetRef);
    setPlanningExists(!!bSnap.exists());
    if (bSnap.exists() && typeof bSnap.data().savingsPct === "number") {
      setSavingsPct(bSnap.data().savingsPct);
    }
  };

  const recalculateCategoryConsumption = async () => {
    if (!userId) return;

    const now = new Date();
    const mesAtual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    const lancamentosRef = collection(firestore, "usuarios", userId, "lancamentos");
    const lancamentosSnapshot = await getDocs(lancamentosRef);
    const lancamentos = lancamentosSnapshot.docs.map((docu) => ({ ...docu.data(), id: docu.id }));

    const categoriasRef = collection(firestore, "usuarios", userId, "categorias");
    const categoriasSnapshot = await getDocs(categoriasRef);
    const categorias = categoriasSnapshot.docs.map((docu) => ({ ...docu.data(), id: docu.id }));

    for (let categoria of categorias) {
      const lancamentosFiltrados = lancamentos.filter((lancamento) => {
        if (!lancamento.data || !lancamento.pago || lancamento.categoria !== categoria.nome) return false;
        const [dia, mes, ano] = lancamento.data.split("/");
        const lancamentoMes = `${ano}-${mes}`;
        return lancamentoMes === mesAtual;
      });

      const consumoAcumulado = lancamentosFiltrados.reduce(
        (total, lancamento) => total + parseCurrencyValue(lancamento.valor),
        0
      );
      const docRef = doc(firestore, "usuarios", userId, "categorias", categoria.id);
      await updateDoc(docRef, { consumo: consumoAcumulado });
    }

    await loadCategorias();
  };

  useEffect(() => {
    loadCategorias();
    loadReceitaMensal();
    recalculateCategoryConsumption();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  /* ===== Tabs ===== */
  const handleTabChange = (event, newValue) => setTabIndex(newValue);

  /* ===== Cálculos de apoio para o wizard ===== */
  const totalPlannedWizard = useMemo(
    () => Object.values(limites).reduce((acc, val) => acc + (Number(val) || 0), 0),
    [limites]
  );
  const receitaNum = parseCurrencyValue(receitaMensal);
  const savingsAmt = receitaNum * savingsPct;
  const restoAposPoupanca = Math.max(0, receitaNum - savingsAmt);
  const restanteWizard = Math.max(0, restoAposPoupanca - totalPlannedWizard);
  const sugestaoFixas = receitaNum * 0.5;
  const sugestaoVariaveis = receitaNum * 0.3;

  /* ===== Wizard Handlers ===== */
  const handleOpenWizard = () => {
    setWizardStep(0); // começa em RECEITA
    setOpenWizard(true);
  };

  const handleSavePlanning = async () => {
    if (!userId) return;

    // totais por tipo
    const fixosTotal = categoriasDespesasFixas.reduce((acc, cat) => acc + (Number(limites[cat.id]) || 0), 0);
    const variaveisTotal = categoriasDespesasVariaveis.reduce((acc, cat) => acc + (Number(limites[cat.id]) || 0), 0);

    const receitaNum = parseCurrencyValue(receitaMensal);
    const poupar = receitaNum * savingsPct;

    // atualiza cards do topo
    localStorage.setItem(
      "budgetSummary",
      JSON.stringify({
        receitaMensal: receitaNum,
        despesasFixas: fixosTotal,
        despesasVariaveis: variaveisTotal,
        poupar,
      })
    );

    // Firestore (somente caminhos permitidos)
    const batch = writeBatch(firestore);

    // resumo do planejamento dentro do doc do usuário
    batch.update(doc(firestore, "usuarios", userId), {
      receitaMensal: receitaNum,
      planning: {
        month: monthKeyNow(),
        income: receitaNum,
        savingsPct,
        savingsAmt: poupar,
        plannedFixed: fixosTotal,
        plannedVar: variaveisTotal,
        updatedAt: new Date().toISOString(),
      },
    });

    // limites por categoria
    [...categoriasDespesasFixas, ...categoriasDespesasVariaveis].forEach((c) => {
      batch.update(doc(firestore, "usuarios", userId, "categorias", c.id), {
        limite: Number(limites[c.id] || 0),
      });
    });

    await batch.commit();

    setPlanningExists(true);
    setOpenWizard(false);
    await loadCategorias();
    await recalculateCategoryConsumption();
  };

  // substitua somente esta função
  const handleDeletePlanning = async () => {
    const uid = getAuth().currentUser?.uid || userId;
    if (!uid) return;

    // batch 1: com tentativa de deletar budgets
    let batch = writeBatch(firestore);

    // zera limites das categorias
    [...categoriasDespesasFixas, ...categoriasDespesasVariaveis].forEach((c) => {
      batch.update(doc(firestore, "usuarios", uid, "categorias", c.id), { limite: 0, consumo: 0 });
    });

    // remove planejamento e zera receita no doc do usuário
    batch.update(doc(firestore, "usuarios", uid), {
      planning: deleteField(),
      receitaMensal: 0,
    });

    // tenta deletar o doc do mês em /budgets
    const budgetsDocRef = doc(firestore, "budgets", uid, "months", monthKeyNow());
    batch.delete(budgetsDocRef);

    try {
      await batch.commit(); // tenta com a exclusão do budgets
    } catch (err) {
      // se for falta de permissão, refaz o commit sem tocar em /budgets
      const msg = String(err?.message || err);
      if (msg.includes("insufficient permissions")) {
        const safeBatch = writeBatch(firestore);
        [...categoriasDespesasFixas, ...categoriasDespesasVariaveis].forEach((c) => {
          safeBatch.update(doc(firestore, "usuarios", uid, "categorias", c.id), { limite: 0, consumo: 0 });
        });
        safeBatch.update(doc(firestore, "usuarios", uid), {
          planning: deleteField(),
          receitaMensal: 0,
        });
        await safeBatch.commit();
      } else {
        throw err; // outro erro: mantenha a exceção
      }
    }

    // limpa cards do resumo e estado
    localStorage.setItem(
      "budgetSummary",
      JSON.stringify({ receitaMensal: 0, despesasFixas: 0, despesasVariaveis: 0, poupar: 0 })
    );

    setLimites({});
    setPlanningExists(false);
    setReceitaMensal("");

    await loadCategorias();
    await recalculateCategoryConsumption();
  };

  /* ===== Modais editar/excluir por categoria ===== */
  const handleOpenDeleteDialog = (categoria) => {
    setCategoriaSelecionada(categoria);
    setOpenDeleteDialog(true);
  };
  const handleOpenEditDialog = (categoria) => {
    setCategoriaSelecionada(categoria);
    setValorLimite(formatNumberToCurrency(categoria.limite || 0));
    setOpenEditDialog(true);
  };
  const handleEditSave = async () => {
    if (categoriaSelecionada) {
      const numericValue = parseCurrencyValue(valorLimite);
      const docRef = doc(firestore, "usuarios", userId, "categorias", categoriaSelecionada.id);
      await updateDoc(docRef, { limite: numericValue });
      await loadCategorias();
      await recalculateCategoryConsumption();
      setOpenEditDialog(false);

      // atualizar cards
      const fixosTotal = categoriasDespesasFixas.reduce((acc, cat) => acc + parseCurrencyValue(cat.limite || 0), 0);
      const variaveisTotal = categoriasDespesasVariaveis.reduce(
        (acc, cat) => acc + parseCurrencyValue(cat.limite || 0),
        0
      );
      localStorage.setItem(
        "budgetSummary",
        JSON.stringify({
          receitaMensal: receitaNum,
          despesasFixas: fixosTotal,
          despesasVariaveis: variaveisTotal,
          poupar: savingsAmt,
        })
      );
    }
  };
  const handleDeleteConfirm = async () => {
    if (categoriaSelecionada) {
      const docRef = doc(firestore, "usuarios", userId, "categorias", categoriaSelecionada.id);
      await updateDoc(docRef, { limite: 0, consumo: 0 });
      await loadCategorias();
      setOpenDeleteDialog(false);
    }
  };

  const handleDeleteCancel = () => setOpenDeleteDialog(false);
  const handleEditCancel = () => setOpenEditDialog(false);

  /* ===== UI helpers existentes ===== */
  const renderStatus = (percentualConsumo) =>
    percentualConsumo >= 80 ? (
      <MDTypography variant="body2" color="warning">
        Alerta
      </MDTypography>
    ) : (
      <MDTypography variant="body2" color="success">
        Seguro
      </MDTypography>
    );

  const renderProgressBarColor = (percentualConsumo) => {
    if (percentualConsumo >= 90) return "#ff0000";
    if (percentualConsumo >= 80) return "#ff9800";
    return "#4caf50";
  };

  const renderCategorias = (categorias) =>
    categorias.map((categoria) => {
      const consumoAtual = categoria.consumo || 0;
      const limite = categoria.limite || 0;
      const percentualConsumo = limite ? (consumoAtual / limite) * 100 : 0;
      const restante = limite - consumoAtual;

      return (
        <Card key={categoria.id} style={{ marginBottom: 12, borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,.06)" }}>
          <MDBox py={1.5} px={2}>
            {/* Linha superior (mobile: nome + progresso + %) */}
            <MDBox
              display="flex"
              flexDirection={{ xs: "row", sm: "row" }}
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <MDBox display="flex" alignItems="center" gap={1}>
                <span style={{ fontSize: 20 }}>{categoria.emoji}</span>
                <MDTypography variant="body1" fontWeight="medium" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                  {categoria.nome}
                </MDTypography>
              </MDBox>

              <MDBox display="flex" alignItems="center" gap={2} width={{ xs: "70%", sm: "45%" }}>
                <div
                  style={{
                    height: 10,
                    width: "100%",
                    backgroundColor: "#e0e0e0",
                    borderRadius: 5,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${percentualConsumo}%`,
                      backgroundColor: renderProgressBarColor(percentualConsumo),
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <MDTypography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ minWidth: 36, textAlign: "right", whiteSpace: "nowrap" }}
                >
                  {percentualConsumo.toFixed(0)}%
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* Linha inferior (mobile: ações) */}
            <MDBox mt={1} display={{ xs: "flex", sm: "none" }} alignItems="center" justifyContent="flex-end" gap={1}>
              <Tooltip title="Editar limite">
                <IconButton size="small" onClick={() => handleOpenEditDialog(categoria)} sx={{ color: "#ff9800" }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zerar limite/consumo">
                <IconButton size="small" onClick={() => handleOpenDeleteDialog(categoria)} sx={{ color: "#ef5350" }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MDBox>

            {/* Layout desktop original (valores + status + ações) */}
            <MDBox
              mt={1}
              display={{ xs: "none", sm: "flex" }}
              alignItems="center"
              gap={1}
              sx={{ color: darkMode ? "white" : "textPrimary" }}
            >
              <MDBox flex={1} />
              <MDBox width="15%" textAlign="right">
                <MDTypography variant="caption" color="text.secondary">
                  Limite
                </MDTypography>
                <MDTypography variant="body2">{formatCurrency(limite, currency)}</MDTypography>
              </MDBox>
              <MDBox width="15%" textAlign="right">
                <MDTypography variant="caption" color="text.secondary">
                  Gasto
                </MDTypography>
                <MDTypography variant="body2">{formatCurrency(consumoAtual, currency)}</MDTypography>
              </MDBox>
              <MDBox width="15%" textAlign="right">
                <MDTypography variant="caption" color="text.secondary">
                  Restante
                </MDTypography>
                <MDTypography variant="body2" color={restante < 0 ? "error" : "text"}>
                  {formatCurrency(restante, currency)}
                </MDTypography>
              </MDBox>
              <MDBox width="10%" textAlign="center">
                {renderStatus(percentualConsumo)}
              </MDBox>
              <MDBox width="10%" textAlign="center">
                <Tooltip title="Editar limite">
                  <IconButton onClick={() => handleOpenEditDialog(categoria)} sx={{ color: "#ff9800", mr: 1 }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Zerar limite/consumo">
                  <IconButton onClick={() => handleOpenDeleteDialog(categoria)} sx={{ color: "#ef5350" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      );
    });

  /* ===== Cards de resumo (mantendo cores/alinhamento originais) ===== */
  const renderSummaryCard = (title, value, index) => {
    const gradients = [
      "linear-gradient(135deg, #f8f8f8, #f0f0f0)",
      "linear-gradient(135deg, #f0f8ff, #e8f0ff)",
      "linear-gradient(135deg, #fff0f0, #ffe8e8)",
      "linear-gradient(135deg, #fff8f0, #fff0e8)",
    ];
    const textColors = [
      darkMode ? "#d3d3d3" : "#606060",
      darkMode ? "#b3d9ff" : "#4060a0",
      darkMode ? "#ffb3b3" : "#a04040",
      darkMode ? "#ffd9b3" : "#a06030",
    ];
    return (
      <Card
        sx={{
          background: darkMode ? "linear-gradient(135deg, #2a2a2a, #3a3a3a)" : gradients[index % gradients.length],
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          borderRadius: "12px",
          padding: "8px",
          textAlign: "center",
          width: "100%",
          height: "80px",
          transition: "transform 0.3s ease",
          "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(0,0,0,0.15)" },
        }}
      >
        <CardContent sx={{ padding: "4px" }}>
          <MDTypography
            variant="caption"
            color={darkMode ? "grey.300" : "text.secondary"}
            fontWeight="medium"
            textTransform="uppercase"
            letterSpacing="0.5px"
            fontSize="0.7rem"
          >
            {title}
          </MDTypography>
          <MDTypography
            variant="body2"
            color={darkMode ? textColors[index % textColors.length] : textColors[index % textColors.length]}
            fontWeight="bold"
            mt={0.5}
            fontSize="0.9rem"
          >
            {formatCurrency(value, currency)}
          </MDTypography>
        </CardContent>
      </Card>
    );
  };

  /* ===== RENDER ===== */
  // valores dos cards (poupar vem da % escolhida, como solicitado)
  const savedSummary = JSON.parse(localStorage.getItem("budgetSummary") || "{}");
  const resumoReceita = parseCurrencyValue(
    savedSummary.receitaMensal !== undefined ? savedSummary.receitaMensal : receitaMensal
  );
  const resumoFixas = parseCurrencyValue(savedSummary.despesasFixas ?? 0);
  const resumoVariaveis = parseCurrencyValue(savedSummary.despesasVariaveis ?? 0);
  const resumoPoupar = parseCurrencyValue(savedSummary.poupar ?? resumoReceita * savingsPct) || 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              {renderSummaryCard("Receita Mensal", resumoReceita, 0)}
            </Grid>
            <Grid item xs={6} sm={3}>
              {renderSummaryCard("Despesas Fixas", resumoFixas, 1)}
            </Grid>
            <Grid item xs={6} sm={3}>
              {renderSummaryCard("Despesa Variável", resumoVariaveis, 2)}
            </Grid>
            <Grid item xs={6} sm={3}>
              {renderSummaryCard("Poupar", resumoPoupar, 3)}
            </Grid>
          </Grid>

          {/* AÇÕES CENTRALIZADAS */}
          <MDBox mt={2} display="flex" justifyContent="center" gap={1} flexWrap="wrap">
            <MDButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenWizard}
              sx={{
                height: 36,
                borderRadius: "10px",
                px: 2,
                backgroundColor: darkMode ? "#444" : "#fff",
                color: darkMode ? "#fff" : "#1976d2",
                border: darkMode ? "1px solid #666" : "1px solid #ddd",
                textTransform: "none",
                fontSize: "0.9rem",
                "&:hover": { backgroundColor: darkMode ? "#555" : "#f0f0f0" },
              }}
            >
              Planejamento Mensal
            </MDButton>
            <MDButton variant="outlined" color="error" onClick={handleDeletePlanning} disabled={!planningExists}>
              Excluir Planejamento
            </MDButton>
          </MDBox>
        </MDBox>

        {/* TABS */}
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          centered
          variant="fullWidth"
          sx={{
            p: 0.5,
            mx: "auto",
            width: { xs: "100%", sm: "auto" },
            borderRadius: 9999,
            background: darkMode ? "rgba(17,25,40,0.55)" : "rgba(255,255,255,0.65)",
            border: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(30,136,229,0.15)",
            boxShadow: darkMode ? "0 6px 18px rgba(0,0,0,0.35)" : "0 6px 18px rgba(30,136,229,0.08)",
            backdropFilter: "saturate(180%) blur(8px)",
            "& .MuiTabs-indicator": { display: "none" },

            "& .MuiTab-root": {
              minHeight: 40,
              borderRadius: 9999,
              textTransform: "none",
              fontWeight: 700,
              fontSize: { xs: "0.9rem", md: "1rem" },
              px: { xs: 1.5, md: 2.5 },
              transition: "all .2s ease",
              color: darkMode ? "rgba(255,255,255,0.72)" : "#5E7FA3",

              "&:hover": {
                backgroundColor: darkMode ? "rgba(255,255,255,0.06)" : "rgba(221,241,255,0.7)",
              },

              "&.Mui-selected": {
                color: darkMode ? "#A6D9FF" : "#1565D8",
                background: darkMode
                  ? "linear-gradient(180deg, rgba(42,56,76,0.9) 0%, rgba(26,33,46,0.9) 100%)"
                  : "linear-gradient(180deg, #E6F5FF 0%, #F4FAFF 100%)",
                border: darkMode ? "1px solid rgba(166,217,255,0.35)" : "1px solid #BFE7FF",
                boxShadow: darkMode
                  ? "0 6px 14px rgba(0,0,0,0.40), inset 0 -2px 0 rgba(166,217,255,0.12)"
                  : "0 6px 14px rgba(21,101,216,0.12), inset 0 -2px 0 rgba(21,101,216,0.06)",
              },
            },
          }}
        >
          <Tab label="Despesas Fixas" />
          <Tab label="Despesas Variáveis" />
        </Tabs>
        <Divider />

        {/* LISTA */}
        <MDBox mt={3}>
          {(tabIndex === 0 ? categoriasDespesasFixas : categoriasDespesasVariaveis).length > 0 ? (
            renderCategorias(tabIndex === 0 ? categoriasDespesasFixas : categoriasDespesasVariaveis)
          ) : (
            <MDTypography variant="body2" align="center" mt={3} color={darkMode ? "white" : "textSecondary"}>
              {tabIndex === 0
                ? "Nenhuma categoria de despesa fixa encontrada."
                : "Nenhuma categoria de despesa variável encontrada."}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>

      {/* ===== WIZARD ===== */}
      <Dialog
        open={openWizard}
        onClose={() => setOpenWizard(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            background: darkMode ? "linear-gradient(135deg,#2a2a2a,#3a3a3a)" : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          Planejamento Mensal
          <IconButton onClick={() => setOpenWizard(false)} sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Passo 0: RECEITA */}
          {wizardStep === 0 && (
            <>
              <MDTypography mb={1} variant="h6">
                Qual sua média de receita mensal?
              </MDTypography>
              <TextField
                label={`Receita Mensal (${currency})`}
                fullWidth
                value={formatCurrencyBR(parseCurrencyValue(receitaMensal))}
                onChange={(e) => {
                  const v = unmaskCurrency(e.target.value);
                  setReceitaMensal(formatNumberToCurrency(v));
                }}
                margin="normal"
                InputProps={{ inputMode: "numeric" }}
              />
            </>
          )}

          {/* Passo 1: POUPANÇA */}
          {wizardStep === 1 && (
            <>
              <MDTypography mb={1} variant="h6">
                Quanto você gostaria de poupar por mês?
              </MDTypography>

              <Slider
                min={0}
                max={0.5}
                step={0.01}
                value={savingsPct}
                onChange={(_, v) => setSavingsPct(v)}
                valueLabelDisplay="on"
                valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
              />

              <MDTypography variant="button" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Sugestão: poupar <strong>20%</strong> da receita para objetivos e metas.
              </MDTypography>

              <MDTypography variant="button" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Isso equivale a <strong>{formatCurrencyBR(savingsAmt)}</strong>.
              </MDTypography>

              <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
                {[0.1, 0.2, 0.3, 0.4, 0.5].map((p) => (
                  <Chip key={p} label={`${p * 100}%`} onClick={() => setSavingsPct(p)} />
                ))}
              </MDBox>
            </>
          )}

          {/* Passo 2: CATEGORIAS */}
          {wizardStep === 2 && (
            <>
              <Stack spacing={1} mb={2}>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  Após reservar a poupança ({formatCurrencyBR(savingsAmt)}), você tem{" "}
                  <strong>{formatCurrencyBR(restoAposPoupanca)}</strong> para distribuir nas categorias.
                </Alert>
                <MDTypography variant="button" color="text.secondary">
                  Sugestão: <strong>50%</strong> da receita para <strong>Despesas Fixas</strong> (
                  {formatCurrencyBR(sugestaoFixas)}) e <strong>30%</strong> para <strong>Variáveis</strong> (
                  {formatCurrencyBR(sugestaoVariaveis)}).
                </MDTypography>
                <MDTypography variant="button" color={restanteWizard >= 0 ? "success.main" : "error.main"}>
                  Saldo restante para alocar: <strong>{formatCurrencyBR(restanteWizard)}</strong>
                </MDTypography>
              </Stack>

              <MDTypography variant="button" color="text.secondary" mb={1} display="block">
                Despesas Fixas
              </MDTypography>
              {categoriasDespesasFixas.map((cat) => (
                <MDBox key={cat.id} mb={1.5} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <MDBox flex={1} display="flex" alignItems="center" gap={1}>
                    <span style={{ fontSize: 20 }}>{cat.emoji || "🏷️"}</span>
                    <MDTypography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>{cat.nome}</MDTypography>
                  </MDBox>
                  <TextField
                    size="small"
                    value={formatCurrencyBR(limites[cat.id] ?? cat.limite ?? 0)}
                    onChange={(e) => setLimites((prev) => ({ ...prev, [cat.id]: unmaskCurrency(e.target.value) }))}
                    inputProps={{ inputMode: "numeric", style: { textAlign: "right" } }}
                    sx={{ width: { xs: 180, sm: 160 }, ml: "auto" }}
                  />
                </MDBox>
              ))}

              <MDTypography variant="button" color="text.secondary" mt={2} mb={1} display="block">
                Despesas Variáveis
              </MDTypography>
              {categoriasDespesasVariaveis.map((cat) => (
                <MDBox key={cat.id} mb={1.5} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <MDBox flex={1} display="flex" alignItems="center" gap={1}>
                    <span style={{ fontSize: 20 }}>{cat.emoji || "🏷️"}</span>
                    <MDTypography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>{cat.nome}</MDTypography>
                  </MDBox>
                  <TextField
                    size="small"
                    value={formatCurrencyBR(limites[cat.id] ?? cat.limite ?? 0)}
                    onChange={(e) => setLimites((prev) => ({ ...prev, [cat.id]: unmaskCurrency(e.target.value) }))}
                    inputProps={{ inputMode: "numeric", style: { textAlign: "right" } }}
                    sx={{ width: { xs: 180, sm: 160 }, ml: "auto" }}
                  />
                </MDBox>
              ))}
            </>
          )}

          {/* Passo 3: REVISÃO (explicativo e sem quebra) */}
          {wizardStep === 3 && (
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  <MDTypography
                    variant="body1"
                    sx={{ whiteSpace: { xs: "normal", sm: "nowrap" }, fontSize: { xs: "0.95rem", sm: "1rem" } }}
                  >
                    Sua receita mensal é <strong>{formatCurrencyBR(receitaNum)}</strong>.
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    sx={{ whiteSpace: { xs: "normal", sm: "nowrap" }, fontSize: { xs: "0.95rem", sm: "1rem" } }}
                  >
                    Você deseja poupar <strong>{formatCurrencyBR(savingsAmt)}</strong> ({(savingsPct * 100).toFixed(0)}
                    %).
                  </MDTypography>
                </CardContent>
              </Card>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <MDTypography variant="caption">Fixas planejadas</MDTypography>
                      <MDTypography sx={{ fontSize: "1rem", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {formatCurrencyBR(
                          categoriasDespesasFixas.reduce((a, c) => a + (Number(limites[c.id]) || 0), 0)
                        )}
                      </MDTypography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <MDTypography variant="caption">Variáveis planejadas</MDTypography>
                      <MDTypography sx={{ fontSize: "1rem", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {formatCurrencyBR(
                          categoriasDespesasVariaveis.reduce((a, c) => a + (Number(limites[c.id]) || 0), 0)
                        )}
                      </MDTypography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {restanteWizard > 0 ? (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Você ainda tem <strong>{formatCurrencyBR(restanteWizard)}</strong> sem categoria definida. Que tal
                  distribuir esse valor antes de salvar?
                </Alert>
              ) : (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  Ótimo! Todo o valor disponível foi alocado nas categorias.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          {wizardStep > 0 && <MDButton onClick={() => setWizardStep((s) => Math.max(0, s - 1))}>Voltar</MDButton>}
          {wizardStep < 3 ? (
            <MDButton onClick={() => setWizardStep((s) => Math.min(3, s + 1))} variant="contained" color="info">
              Próximo
            </MDButton>
          ) : (
            <MDButton onClick={handleSavePlanning} variant="contained" color="info">
              Salvar
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      {/* ===== Modais Excluir / Editar Categoria ===== */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            background: darkMode ? "linear-gradient(135deg, #2a2a2a, #3a3a3a)" : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ color: darkMode ? "white" : "#1976d2", fontWeight: "bold", position: "relative" }}>
          Confirmação de Exclusão
          <IconButton
            onClick={handleDeleteCancel}
            sx={{ color: darkMode ? "grey.300" : "grey.600", position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body1" color={darkMode ? "white" : "text.primary"}>
            Tem certeza de que deseja zerar o limite e o consumo desta categoria?
          </MDTypography>
        </DialogContent>
        <DialogActions
          sx={{ p: 3, borderTop: "1px solid", borderColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)" }}
        >
          <MDButton variant="contained" color="error" onClick={handleDeleteConfirm} sx={{ borderRadius: "8px", px: 3 }}>
            Excluir
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditDialog}
        onClose={handleEditCancel}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            background: darkMode ? "linear-gradient(135deg, #2a2a2a, #3a3a3a)" : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ color: darkMode ? "white" : "#1976d2", fontWeight: "bold", position: "relative" }}>
          Editar Limite
          <IconButton
            onClick={handleEditCancel}
            sx={{ color: darkMode ? "grey.300" : "grey.600", position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body1" color={darkMode ? "white" : "text.primary"} mb={2}>
            Ajuste o limite para {categoriaSelecionada?.nome} ({categoriaSelecionada?.emoji}):
          </MDTypography>
          <TextField
            label="Novo Limite"
            value={valorLimite}
            onChange={(e) => {
              const rawValue = e.target.value.replace(/[^0-9]/g, "");
              const numericValue = rawValue === "" ? 0 : parseInt(rawValue, 10) / 100;
              setValorLimite(formatNumberToCurrency(numericValue));
            }}
            fullWidth
            variant="outlined"
            margin="normal"
            InputProps={{ inputMode: "numeric" }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
        </DialogContent>
        <DialogActions
          sx={{ p: 3, borderTop: "1px solid", borderColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)" }}
        >
          <MDButton variant="contained" color="info" onClick={handleEditSave} sx={{ borderRadius: "8px", px: 3 }}>
            Salvar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default LimiteGastos;
